import React from 'react';
import { fieldGenerator, getErrorOnField } from 'utilities/form';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
    Row,
    Form,
    Button,
    Col,
    Spin,
    Tag,
    message,
    Upload,
    Icon,
    Modal,
    Input,
    Alert,
    Descriptions,
    Card,
} from 'antd';
import { INPUT_TYPES } from 'constants';
import { NACH_MODES_TYPE, NACH_STATUS } from 'constants/enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { CaseService } from '../services';
import ConfirmButton from '../components/confirm-button';
import { Map } from 'immutable';
import {
    buildModeDropdownOptions,
    buildMandateSummary,
    canCancelStatus,
    isSuccessStatus,
    isInProgressStatus,
    getDisplayStatusLabel,
    getStatusTagColor,
    getPhysicalChequeRowCount,
    getNoNachPdcRowCount,
    getNoNachPdcEmlDocumentUrl,
    getNoNachPdcTermLoanInstalments,
    getPhysicalTermLoanInstalments,
    resolveEmiDateForNachRequest,
    getNachSectionTooltip,
} from 'utilities/nach-mandate-utils';

const MANDATE_API = NACH_MODES_TYPE.getIn(['API_MANDATE', 'value']);
const MANDATE_PHYSICAL = NACH_MODES_TYPE.getIn(['PHYSICAL_MANDATE', 'value']);
const MANDATE_NO_PDC = NACH_MODES_TYPE.getIn(['NO_NACH_PDC_ONLY', 'value']);

class NachMandateForm extends React.Component {
    fieldGenerator = fieldGenerator.bind(this.props.form);
    getErrorOnField = getErrorOnField.bind(this.props.form);

    state = {
        fileList: [],
        emlFile: null,
        physicalCheques: [{}, {}, {}],
        physicalGenerated: false,
        pdcCheques: [],
        pdcGenerated: false,
        uploadLoader: false,
        cancelNachLoader: false,
        pollLoader: false,
        loading: false,
        mandateType: null,
        physicalDownloadLoader: false,
        physicalChequesSaveLoader: false,
    };

    componentDidMount() {
        this.syncFromResultRow(this.props.resultRow);
        const initial =
            this.props.form.getFieldValue('mandate_type') ||
            (this.props.values && this.props.values.get('mandate_type'));
        if (initial != null) {
            this.onMandateTypeChange(initial);
        } else if (this.getEffectiveMandateType() === MANDATE_NO_PDC) {
            this.syncNoNachPdcFromJourney();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.resultRow !== this.props.resultRow) {
            this.syncFromResultRow(this.props.resultRow);
        }
        if (prevProps.categoryJourney !== this.props.categoryJourney) {
            this.syncFromJourneyPhysical();
            this.syncNoNachPdcFromJourney();
        }
        if (prevProps.values !== this.props.values && this.props.values) {
            const next = this.props.values.get('mandate_type');
            if (next != null && next !== this.state.mandateType) {
                this.onMandateTypeChange(next);
            }
        }
    }

    getEffectiveMandateType = () => {
        const fromState = this.state.mandateType;
        if (fromState != null) return fromState;
        return this.props.form.getFieldValue('mandate_type');
    };

    formatEmiDate = (d) => {
        if (d == null || d === '') return '—';
        const s = String(d);
        if (/^\d{8}$/.test(s)) {
            return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
        }
        return s;
    };

    isChequeLikeObject = (obj) => {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
        return (
            'cheque_number' in obj ||
            'beneficiary_name' in obj ||
            'cheque_amount' in obj ||
            'emi_date' in obj ||
            'due_date' in obj ||
            'emi_amount' in obj ||
            'name' in obj
        );
    };

    /** Always enforce Physical row count: 3 for SECURITY / EMI sections */
    normalizePhysicalChequeRows = (rows, nachRequestType) => {
        const rowCount = getPhysicalChequeRowCount(nachRequestType);
        const normalized = (rows || []).slice(0, rowCount);
        while (normalized.length < rowCount) {
            normalized.push({});
        }
        return normalized;
    };

    getPhysicalChequeFromJourney = () => {
        const physical =
            this.props.categoryJourney &&
            this.props.categoryJourney.modes &&
            this.props.categoryJourney.modes.PHYSICAL;
        if (!physical) return null;

        // Only read dedicated cheque payloads — never the whole PHYSICAL mode object
        const raw =
            physical.cheque_details ||
            physical.cheque_json ||
            physical.nach_cheque_details;

        if (!raw) return null;

        if (this.isChequeLikeObject(raw)) {
            const mapped = this.mapChequeRowFromApi(raw);
            return mapped ? [mapped] : null;
        }

        const list = this.normalizeChequeList(raw).filter((r) => this.isChequeLikeObject(r));
        if (!list.length) return null;
        return list.map((r) => this.mapChequeRowFromApi(r)).filter(Boolean);
    };

    /** cheque_json from modes.PHYSICAL or modes.NO_NACH_PDC (EMI-1 / EMI-2) */
    getEmiChequeJsonRows = (modeName) => {
        const { categoryJourney, resultRow } = this.props;
        const modes = (categoryJourney && categoryJourney.modes) || {};
        const mode =
            modeName === 'PHYSICAL' ? modes.PHYSICAL : modes.NO_NACH_PDC || modes.no_nach_pdc;
        const nachDetails = (resultRow && resultRow.nach_details) || {};

        let source = null;
        if (mode && mode.cheque_json != null) {
            source = mode.cheque_json;
        } else if (categoryJourney && categoryJourney.cheque_json != null) {
            source = categoryJourney.cheque_json;
        } else if (nachDetails.cheque_json != null) {
            source = nachDetails.cheque_json;
        }

        if (source == null) return [];

        const list = this.normalizeChequeList(source).filter((r) => this.isChequeLikeObject(r));
        if (!list.length) return [];
        return list.map((r) => this.mapChequeRowFromApi(r)).filter(Boolean);
    };

    /**
     * EMI-1 / EMI-2 rows: cheque_json on the mode object when not null, else term_loan_instalment.
     * modeName: 'PHYSICAL' | 'NO_NACH_PDC'
     */
    getEmiChequeRowsForMode = (modeName) => {
        const { nachRequestType, instalmentFrequency, totalEmiCount } = this.props;
        const rowCount = getNoNachPdcRowCount(
            nachRequestType,
            instalmentFrequency,
            totalEmiCount
        );

        const chequeJsonRows = this.getEmiChequeJsonRows(modeName);
        if (chequeJsonRows.length) {
            return chequeJsonRows;
        }

        const instalments =
            modeName === 'PHYSICAL'
                ? this.getPhysicalInstalmentSource()
                : this.getNoNachPdcInstalmentSource();
        if (instalments.length) {
            return this.mapNoNachPdcInstalmentRows(instalments);
        }

        return Array.from({ length: rowCount }, () => ({}));
    };

    getEmiPdcChequeRows = () => this.getEmiChequeRowsForMode('PHYSICAL');

    getPhysicalChequeRows = () => {
        const { resultRow, isTermLoan, nachRequestType } = this.props;

        if (nachRequestType === 'EMI-1' || nachRequestType === 'EMI-2') {
            return this.getEmiPdcChequeRows();
        }

        const rowCount = getPhysicalChequeRowCount(nachRequestType);

        const fromJourney = this.getPhysicalChequeFromJourney();
        if (fromJourney && fromJourney.length) {
            return this.normalizePhysicalChequeRows(fromJourney, nachRequestType);
        }

        const instalments =
            (resultRow && resultRow.nach_details && resultRow.nach_details.nach_cheque_details) ||
            [];

        // term_loan_instalment applies to EMI sections only, not Security
        const termLoanInstalments =
            nachRequestType !== 'SECURITY' && resultRow && resultRow.term_loan_instalment
                ? resultRow.term_loan_instalment
                : [];

        const source = instalments.length ? instalments : termLoanInstalments;

        if (isTermLoan && nachRequestType !== 'SECURITY' && source.length) {
            const mapped = source.slice(0, rowCount).map((row) => ({
                ...row,
                due_date: this.formatEmiDate(row.due_date),
                emi_amount: row.emi_amount != null ? row.emi_amount : '100000',
            }));
            return this.normalizePhysicalChequeRows(mapped, nachRequestType);
        }

        return Array.from({ length: rowCount }, () => ({}));
    };

    mapNoNachPdcInstalmentRows = (instalments) =>
        instalments
            .map((row) => this.mapChequeRowFromApi(row))
            .filter(Boolean)
            .map((row) => ({
                ...row,
                due_date:
                    row.due_date != null && row.due_date !== ''
                        ? this.formatEmiDate(row.due_date)
                        : undefined,
                emi_amount:
                    row.emi_amount != null && row.emi_amount !== ''
                        ? row.emi_amount
                        : '100000',
            }));

    /** Cheque rows from cheque_json on the NO_NACH_PDC mode (Security) */
    getNoNachPdcChequeJsonRows = () => {
        const fromChequeJson = this.getEmiChequeJsonRows('NO_NACH_PDC');
        if (fromChequeJson.length) return fromChequeJson;

        const { categoryJourney, resultRow } = this.props;
        const nachDetails = (resultRow && resultRow.nach_details) || {};
        const source = nachDetails.nach_cheque_details || null;
        if (source == null) return [];

        const list = this.normalizeChequeList(source).filter((r) => this.isChequeLikeObject(r));
        if (!list.length) return [];
        return list.map((r) => this.mapChequeRowFromApi(r)).filter(Boolean);
    };

    getPhysicalInstalmentSource = () => {
        const { categoryJourney, resultRow, nachRequestType } = this.props;
        if (nachRequestType === 'SECURITY') return [];

        const fromMode = getPhysicalTermLoanInstalments(categoryJourney);
        if (fromMode.length) return fromMode;

        if (resultRow && Array.isArray(resultRow.term_loan_instalment) && resultRow.term_loan_instalment.length) {
            return resultRow.term_loan_instalment.filter(Boolean);
        }

        const nachDetails = resultRow && resultRow.nach_details;
        if (nachDetails && Array.isArray(nachDetails.term_loan_instalment) && nachDetails.term_loan_instalment.length) {
            return nachDetails.term_loan_instalment.filter(Boolean);
        }

        return [];
    };

    getNoNachPdcInstalmentSource = () => {
        const { categoryJourney, resultRow, nachRequestType } = this.props;
        if (nachRequestType === 'SECURITY') return [];

        const fromMode = getNoNachPdcTermLoanInstalments(categoryJourney);
        if (fromMode.length) return fromMode;

        if (resultRow && Array.isArray(resultRow.term_loan_instalment) && resultRow.term_loan_instalment.length) {
            return resultRow.term_loan_instalment.filter(Boolean);
        }

        const nachDetails = resultRow && resultRow.nach_details;
        if (nachDetails && Array.isArray(nachDetails.term_loan_instalment) && nachDetails.term_loan_instalment.length) {
            return nachDetails.term_loan_instalment.filter(Boolean);
        }

        return [];
    };

    getNoNachPdcRows = () => {
        const { nachRequestType, instalmentFrequency, totalEmiCount } = this.props;
        const rowCount = getNoNachPdcRowCount(
            nachRequestType,
            instalmentFrequency,
            totalEmiCount
        );

        if (nachRequestType === 'SECURITY') {
            const chequeJsonRows = this.getNoNachPdcChequeJsonRows();
            if (chequeJsonRows.length) {
                return chequeJsonRows;
            }
            return Array.from({ length: rowCount }, () => ({}));
        }

        return this.getEmiChequeRowsForMode('NO_NACH_PDC');
    };

    syncNoNachPdcFromJourney = () => {
        if (this.getEffectiveMandateType() !== MANDATE_NO_PDC) return;

        const { nachRequestType, instalmentFrequency, totalEmiCount } = this.props;

        if (nachRequestType === 'SECURITY') {
            const chequeJsonRows = this.getNoNachPdcChequeJsonRows();
            this.setState({
                pdcCheques: chequeJsonRows.length
                    ? chequeJsonRows
                    : Array.from({ length: getNoNachPdcRowCount(nachRequestType, instalmentFrequency, totalEmiCount) }, () => ({})),
                pdcGenerated: true,
            });
            return;
        }

        const rows = this.getEmiChequeRowsForMode('NO_NACH_PDC');
        const hasContent = rows.some(
            (r) => r && (r.cheque_number || r.name || r.due_date || r.emi_amount)
        );
        if (!hasContent) return;

        this.setState({ pdcCheques: rows, pdcGenerated: true });
    };

    onMandateTypeChange = (mandateType) => {
        const physicalCheques = this.getPhysicalChequeRows();
        const nextState = {
            mandateType,
            physicalCheques,
            physicalGenerated: mandateType === MANDATE_PHYSICAL,
            fileList: [],
            emlFile: null,
            pdcGenerated: false,
            pdcCheques: [],
        };
        this.setState(nextState, () => {
            if (mandateType === MANDATE_NO_PDC) {
                this.syncFromResultRow(this.props.resultRow);
                this.syncNoNachPdcFromJourney();
            }
            if (mandateType === MANDATE_PHYSICAL) {
                this.syncFromJourneyPhysical();
            }
        });
    };

    mapChequeRowFromApi = (row) => {
        if (!row) return null;
        return {
            ...row,
            name: row.beneficiary_name || row.name || '',
            cheque_number: row.cheque_number || '',
            emi_amount:
                row.cheque_amount != null
                    ? row.cheque_amount
                    : row.emi_amount != null
                    ? row.emi_amount
                    : '',
            due_date: row.due_date ? this.formatEmiDate(row.due_date) : row.emi_date,
        };
    };

    normalizeChequeList = (source) => {
        if (!source) return [];
        if (Array.isArray(source)) return source.filter(Boolean);
        if (typeof source === 'object') return Object.values(source).filter(Boolean);
        return [];
    };

    syncFromJourneyPhysical = () => {
        const mandateType = this.getEffectiveMandateType();
        if (mandateType !== MANDATE_PHYSICAL) return;

        const { nachRequestType } = this.props;
        if (nachRequestType === 'EMI-1' || nachRequestType === 'EMI-2') {
            const rows = this.getEmiChequeRowsForMode('PHYSICAL');
            const hasContent = rows.some(
                (r) => r && (r.cheque_number || r.name || r.due_date || r.emi_amount)
            );
            if (hasContent) {
                this.setState({ physicalCheques: rows, physicalGenerated: true });
            }
            return;
        }

        const fromJourney = this.getPhysicalChequeFromJourney();
        if (!fromJourney || !fromJourney.length) return;

        this.setState({
            physicalCheques: this.normalizePhysicalChequeRows(
                fromJourney,
                nachRequestType
            ),
            physicalGenerated: true,
        });
    };

    syncFromResultRow = (resultRow) => {
        if (!resultRow) return;
        const nachDetails = resultRow.nach_details || {};
        const cheques = this.normalizeChequeList(
            nachDetails.nach_cheque_details ||
                nachDetails.cheque_json ||
                resultRow.term_loan_instalment
        );
        const mapped = cheques
            .map((row) => this.mapChequeRowFromApi(row))
            .filter(Boolean);

        if (!mapped.length) return;

        const mandateType = this.getEffectiveMandateType();
        const { nachRequestType } = this.props;
        if (mandateType === MANDATE_PHYSICAL || nachDetails.mandate_type === MANDATE_PHYSICAL) {
            if (nachRequestType === 'EMI-1' || nachRequestType === 'EMI-2') {
                const rows = this.getEmiChequeRowsForMode('PHYSICAL');
                const hasContent = rows.some(
                    (r) => r && (r.cheque_number || r.name || r.due_date || r.emi_amount)
                );
                if (hasContent) {
                    this.setState({ physicalCheques: rows, physicalGenerated: true });
                    return;
                }
            }
            this.setState({
                physicalCheques: this.normalizePhysicalChequeRows(mapped, nachRequestType),
                physicalGenerated: true,
            });
            return;
        }

        if (mandateType === MANDATE_NO_PDC || nachDetails.mandate_type === MANDATE_NO_PDC) {
            if (nachRequestType === 'EMI-1' || nachRequestType === 'EMI-2') {
                const rows = this.getEmiChequeRowsForMode('NO_NACH_PDC');
                const hasContent = rows.some(
                    (r) => r && (r.cheque_number || r.name || r.due_date || r.emi_amount)
                );
                if (hasContent) {
                    this.setState({ pdcCheques: rows, pdcGenerated: true });
                    return;
                }
            } else if (mapped.length) {
                this.setState({
                    pdcCheques: mapped,
                    pdcGenerated: true,
                });
                return;
            }
            this.syncNoNachPdcFromJourney();
        }
    };

    getSelectedModeJourney = () => {
        const { categoryJourney } = this.props;
        const mandateType = this.getEffectiveMandateType();
        if (!categoryJourney || !categoryJourney.modes) return null;
        if (mandateType === MANDATE_API) return categoryJourney.modes.API;
        if (mandateType === MANDATE_PHYSICAL) return categoryJourney.modes.PHYSICAL;
        return categoryJourney.modes.NO_NACH_PDC;
    };

    getNachDetails = () => {
        const { resultRow } = this.props;
        const details = resultRow && resultRow.nach_details;
        const mandateType = this.getEffectiveMandateType();
        if (details && details.mandate_type === mandateType) return details;
        return null;
    };

    getBankNachId = () => {
        const modeJourney = this.getSelectedModeJourney();
        const nachDetails = this.getNachDetails();
        const physicalJourney =
            this.props.categoryJourney &&
            this.props.categoryJourney.modes &&
            this.props.categoryJourney.modes.PHYSICAL;

        return (
            (modeJourney && (modeJourney.bank_nach_id || modeJourney.nach_request_id)) ||
            (physicalJourney && (physicalJourney.bank_nach_id || physicalJourney.nach_request_id)) ||
            (nachDetails && (nachDetails.companybank_nach_id || nachDetails.bank_nach_id)) ||
            null
        );
    };

    mapRowToChequeDetail = (row) => {
        const detail = {
            cheque_number: String(row.cheque_number).trim(),
        };
        // Security does not send an amount in the payload.
        if (this.props.nachRequestType !== 'SECURITY') {
            detail.cheque_amount = Number(row.emi_amount || row.cheque_amount || 100000);
        }
        if (row.name && row.name.trim()) {
            detail.beneficiary_name = row.name.trim();
        }
        if (row.due_date) {
            detail.emi_date = row.due_date;
        }
        return detail;
    };

    validatePhysicalCheques = (rows) => {
        if (!rows || !rows.length) {
            message.error('Add at least one cheque row');
            return false;
        }

        // Amount field is removed for the Security physical mandate, so it isn't required there.
        const amountRequired = this.props.nachRequestType !== 'SECURITY';

        const allValid = rows.every((row, index) => {
            const isNameValid = row.name && row.name.trim() !== '';
            const isChequeNumberValid = row.cheque_number && /^\d{6}$/.test(String(row.cheque_number));
            const rawAmount = row.emi_amount != null ? row.emi_amount : row.cheque_amount;
            const hasAmount = rawAmount != null && String(rawAmount).trim() !== '';
            const amount = Number(rawAmount);
            const isAmountValid = !amountRequired || (hasAmount && !Number.isNaN(amount) && amount > 0);

            if (!isNameValid) {
                message.error(`Row ${index + 1}: Beneficiary name is required`);
            } else if (!isChequeNumberValid) {
                message.error(`Row ${index + 1}: Cheque number must be exactly 6 digits`);
            } else if (!isAmountValid) {
                message.error(`Row ${index + 1}: Cheque amount is required`);
            }

            return isNameValid && isChequeNumberValid && isAmountValid;
        });

        return allValid;
    };

    savePhysicalChequeDetails = () => {
        const bankNachId = this.getBankNachId();
        if (!bankNachId) {
            message.error('Physical NACH must be triggered before saving cheque details');
            return;
        }

        const { physicalCheques } = this.state;
        if (!this.validatePhysicalCheques(physicalCheques)) return;

        const mapped = physicalCheques.map((row) => this.mapRowToChequeDetail(row));
        const cheque_details = mapped.length === 1 ? mapped[0] : mapped;

        this.setState({ physicalChequesSaveLoader: true });
        CaseService.savePhysicalNachChequeDetails(this.props.caseId, {
            bank_nach_id: bankNachId,
            cheque_details,
        })
            .then((resp) => {
                message.success(
                    (resp.data && resp.data.message) || 'Cheque details saved successfully'
                );
                this.props.updateNachData();
            })
            .catch((err) => {
                const msg =
                    (err && err.Message && err.Message.data && err.Message.data.message) ||
                    (err && err.response && err.response.data && err.response.data.message) ||
                    (err && err.message) ||
                    'Failed to save cheque details';
                message.error(msg);
            })
            .finally(() => this.setState({ physicalChequesSaveLoader: false }));
    };

    getPhysicalMandateId = () => {
        const nachDetails = this.getNachDetails();
        if (nachDetails && nachDetails.mandate_id) return nachDetails.mandate_id;

        const physicalJourney =
            this.props.categoryJourney &&
            this.props.categoryJourney.modes &&
            this.props.categoryJourney.modes.PHYSICAL;
        if (physicalJourney && physicalJourney.mandate_id) return physicalJourney.mandate_id;

        const { resultRow } = this.props;
        const rowDetails = resultRow && resultRow.nach_details;
        if (rowDetails && rowDetails.mandate_id) return rowDetails.mandate_id;

        const summary = buildMandateSummary(
            this.props.categoryJourney,
            MANDATE_PHYSICAL,
            resultRow
        );
        if (summary && summary.mandate_id && summary.mandate_id !== '—') {
            return summary.mandate_id;
        }

        return null;
    };

    downloadPhysicalForm = () => {
        const mandateId = this.getPhysicalMandateId();
        if (!mandateId) {
            message.error('Mandate ID is not available to download the physical form');
            return;
        }

        this.setState({ physicalDownloadLoader: true });
        CaseService.downloadPhysicalMandateDocument(this.props.caseId, mandateId)
            .then((res) => {
                const contentType =
                    (res.headers && res.headers['content-type']) || 'application/pdf';
                const blob = new Blob([res.data], { type: contentType });
                const disposition = res.headers && res.headers['content-disposition'];
                let fileName = `physical-mandate-${mandateId}.pdf`;
                if (disposition) {
                    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (match && match[1]) {
                        fileName = match[1].replace(/['"]/g, '');
                    }
                }
                const elem = window.document.createElement('a');
                elem.href = window.URL.createObjectURL(blob);
                elem.download = fileName;
                document.body.appendChild(elem);
                elem.click();
                document.body.removeChild(elem);
                window.URL.revokeObjectURL(elem.href);
                message.success('Physical form downloaded');
            })
            .catch((err) => {
                const msg =
                    (err && err.Message && err.Message.data && err.Message.data.message) ||
                    (err && err.message) ||
                    'Failed to download physical form';
                message.error(msg);
            })
            .finally(() => this.setState({ physicalDownloadLoader: false }));
    };

    getModeStatus = () => {
        const modeJourney = this.getSelectedModeJourney();
        const nachDetails = this.getNachDetails();
        const statusCode =
            (nachDetails && nachDetails.nach_status) != null
                ? nachDetails.nach_status
                : modeJourney && modeJourney.status;
        const statusLabel = getDisplayStatusLabel(
            statusCode,
            modeJourney && modeJourney.status_label
        );
        return { statusCode, statusLabel };
    };

    renderInlineStatusBadge = () => {
        const { statusCode, statusLabel } = this.getModeStatus();
        return (
            <Tag color={getStatusTagColor(statusCode, statusLabel)} className="nach-mandate-status-tag">
                {statusLabel}
            </Tag>
        );
    };

    getCaseEnachDetails = (e, mandate_type) => {
        e.preventDefault();
        this.props.form.validateFields((err) => {
            if (!err) {
                const { bank_account, nachRequestType, emiDate, categoryJourney, emiDates, resultRow } =
                    this.props;
                const emi_date = resolveEmiDateForNachRequest({
                    nachRequestType,
                    payloadEmiDate: emiDate,
                    category: categoryJourney,
                    emiDates: emiDates || [],
                    resultsList: resultRow ? [resultRow] : [],
                });
                this.props.onSubmit(
                    {
                        mandate_type,
                        bank_account_id: bank_account.id,
                        associate_id: this.props.form.getFieldValue('associate'),
                        nach_request_type: nachRequestType,
                        emi_date,
                    },
                    this.props.form.getFieldsValue()
                );
            }
        });
    };

    copyToClipboard = (url) => {
        navigator.clipboard.writeText(url);
        message.success('Copied to clipboard');
    };

    handleChequeInputChange = (rowsKey, index, field, value) => {
        const rows = [...this.state[rowsKey]];
        rows[index] = { ...rows[index], [field]: value };
        this.setState({ [rowsKey]: rows });
    };

    pollDigioStatus = () => {
        this.setState({ pollLoader: true });
        CaseService.pollEnach(this.props.caseId)
            .then(() => {
                message.success('Status updated');
                this.props.updateNachData();
            })
            .catch(() => message.error('Failed to check status'))
            .finally(() => this.setState({ pollLoader: false }));
    };

    cancelNachMandate = () => {
        const modeJourney = this.getSelectedModeJourney();
        const nachDetails = this.getNachDetails();
        const bankNachId =
            (modeJourney && modeJourney.nach_request_id) ||
            (nachDetails && nachDetails.companybank_nach_id);

        if (!bankNachId) {
            message.error('No active NACH to cancel');
            return;
        }

        const { nachRequestType, emiDate, categoryJourney } = this.props;
        this.setState({ cancelNachLoader: true });
        CaseService.cancelNachMandate(this.props.caseId, {
            bank_nach_id: bankNachId,
            nach_request_type: nachRequestType,
            // emi_date: emiDate != null ? emiDate : categoryJourney.emi_date,
        })
            .then(() => {
                message.success('E-Nach Successfully Cancelled');
                this.props.updateNachData();
            })
            .catch(() => message.error('E-Nach Cancel Request Failed'))
            .finally(() => this.setState({ cancelNachLoader: false }));
    };

    handlePhysicalUpload = (file) => {
        this.setState({ fileList: [file] });
        const { bank_account, nachRequestType, emiDate, categoryJourney } = this.props;
        Modal.confirm({
            title: 'Are you sure you want to Upload Physical Enach',
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: (close) => {
                this.setState({ uploadLoader: true });
                const formData = new FormData();
                formData.append('associate_id', this.props.form.getFieldValue('associate'));
                formData.append('account_id', bank_account.id);
                formData.append('nach_request_type', nachRequestType);
                formData.append('emi_date', emiDate != null ? emiDate : categoryJourney.emi_date);
                formData.append('file', file);
                CaseService.uploadPhysicalNach(this.props.caseId, formData)
                    .then(() => {
                        message.success('Physical Mandate Triggered Successfully');
                        this.props.updateNachData();
                    })
                    .catch((err) => {
                        message.error(
                            (err.Message && err.Message.data && err.Message.data.message) ||
                            'Something Went Wrong'
                        );
                    })
                    .finally(() => {
                        this.setState({ uploadLoader: false, fileList: [] });
                    });
                close();
            },
        });
        return false;
    };

    generatePhysicalCheques = () => {
        this.setState({ physicalGenerated: true, physicalCheques: this.getPhysicalChequeRows() });
    };

    getExistingEmlDocumentUrl = () =>
        getNoNachPdcEmlDocumentUrl(this.props.categoryJourney, this.props.resultRow);

    hasEmlForGenerate = () =>
        !!(this.state.emlFile || this.getExistingEmlDocumentUrl());

    handleEmlUpload = (file) => {
        this.setState({ emlFile: file, fileList: [file], pdcGenerated: false, pdcCheques: [] });
        return false;
    };

    triggerNoNachPdc = () => {
        const { bank_account, nachRequestType, emiDate, categoryJourney } = this.props;
        if (!this.hasEmlForGenerate()) {
            message.error('EML file is required before Generate');
            return;
        }

        const today = new Date();
        const collection_date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const emi_date = emiDate != null ? emiDate : categoryJourney.emi_date;
        const associate = this.props.form.getFieldValue('associate');

        const formData = new FormData();
        formData.append('nach_request_type', nachRequestType);
        formData.append('nach_mode', 'no_nach_pdc');
        formData.append('nach_type', String(MANDATE_NO_PDC));
        formData.append('associate', String(associate));
        formData.append('account', bank_account.account_number);
        formData.append('collection_date', collection_date);
        formData.append('amount', '10000000');
        formData.append('emi_date', String(emi_date));
        if (this.state.emlFile) {
            formData.append('eml_file', this.state.emlFile);
        }

        this.setState({ loading: true });
        this.props
            .triggerEnach({ _formData: formData, emi_date, nach_request_type: nachRequestType })
            .then(() => {
                this.props.updateNachData();
                const rows = this.getNoNachPdcRows();
                this.setState({ pdcGenerated: true, pdcCheques: rows, loading: false }, () => {
                    this.syncNoNachPdcFromJourney();
                });
            })
            .catch(() => this.setState({ loading: false }));
    };

    updateNoNachPDC = () => {
        const { pdcCheques } = this.state;
        // Amount field is hidden for Security PDC, so it isn't required there.
        const amountRequired = this.props.nachRequestType !== 'SECURITY';
        const isValid = pdcCheques.every((inst) => {
            const isNameValid = inst.name && inst.name.trim() !== '';
            const isChequeNumberValid = inst.cheque_number && /^\d{6}$/.test(inst.cheque_number);
            const rawAmount = inst.emi_amount != null ? inst.emi_amount : inst.cheque_amount;
            const isAmountValid =
                !amountRequired ||
                (rawAmount != null && String(rawAmount).trim() !== '' && Number(rawAmount) > 0);
            if (!isNameValid) message.error('Name cannot be empty or just whitespace');
            if (!isChequeNumberValid) message.error('Cheque number must be numeric and exactly 6 digits');
            if (!isAmountValid) message.error('Amount is required and must be greater than 0');
            return isNameValid && isChequeNumberValid && isAmountValid;
        });
        if (!isValid) return;

        const { caseId, nachRequestType, emiDate, categoryJourney, emiDates, resultRow } =
            this.props;
        const emi_date = resolveEmiDateForNachRequest({
            nachRequestType,
            payloadEmiDate: emiDate,
            category: categoryJourney,
            emiDates: emiDates || [],
            resultsList: resultRow ? [resultRow] : [],
        });
        if (emi_date == null || emi_date === '') {
            message.error('EMI date is not available. Refresh NACH status and try again.');
            return;
        }
        this.setState({ loading: true });
        const isSecurity = nachRequestType === 'SECURITY';
        CaseService.noNachPDCTrigger(
            caseId,
            emi_date,
            pdcCheques.map((row, index) => {
                const detail = {
                    id: index + 1,
                    name: row.name,
                    cheque_number: row.cheque_number,
                };
                if (!isSecurity) {
                    const rowEmiDate = row.due_date || row.emi_date;
                    if (rowEmiDate) {
                        detail.emi_date = rowEmiDate;
                    }
                    const rawAmount =
                        row.emi_amount != null ? row.emi_amount : row.cheque_amount;
                    detail.emi_amount = Number(rawAmount);
                }
                return detail;
            }),
            nachRequestType
        )
            .then(() => {
                message.success('Nach Detail Updated');
                this.props.updateNachData();
            })
            .catch(() => message.error('Submit failed'))
            .finally(() => this.setState({ loading: false }));
    };

    getMandateTypeLabel = (mandateType) => {
        const match = NACH_MODES_TYPE.find((item) => item.value === mandateType);
        return match ? match.label : '—';
    };

    getTransferredTo = (nachDetails) => {
        if (!nachDetails || !nachDetails.digio_respone) return null;
        const d = nachDetails.digio_respone.data;
        return d && d.bank_details && d.bank_details.bank_name;
    };

    renderStatusTag = () => {
        const modeJourney = this.getSelectedModeJourney();
        const nachDetails = this.getNachDetails();
        const status = (modeJourney && modeJourney.status) || (nachDetails && nachDetails.nach_status);
        if (status === NACH_STATUS.get('VERIFIED')) {
            return (
                <span className="ml-10">
                    <Tag color="green">Verified</Tag>
                </span>
            );
        }
        if (status === NACH_STATUS.get('BANK_ACCEPTED')) {
            return (
                <span className="ml-10">
                    <Tag color="green">Bank Confirmed</Tag>
                </span>
            );
        }
        return null;
    };

    renderChequeTable = (rowsKey, rows, options = {}) => {
        const {
            showEmiDate,
            showAmount,
            sectionLabel,
            showSave,
            onSave,
            saveLoading,
            saveDisabled,
        } = options;
        const _showEmiDate = !!showEmiDate;
        const _showAmount = !!showAmount;

        return (
            <div className="nach-mandate-cheque-block">
                {sectionLabel && <p className="nach-mandate-cheque-label">{sectionLabel}</p>}
                <table className="nach-mandate-cheque-table">
                    <thead>
                        <tr>
                            {_showEmiDate && <th>EMI Date</th>}
                            {!_showEmiDate && <th>S. No.</th>}
                            <th>Beneficiary Name</th>
                            <th>Cheque Number</th>
                            {_showAmount && <th>Amount</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index}>
                                {_showEmiDate ? (
                                    <td>{row.due_date || '—'}</td>
                                ) : (
                                    <td>{index + 1}</td>
                                )}
                                <td>
                                    <Input
                                        placeholder="Beneficiary name"
                                        value={row.name || ''}
                                        onChange={(e) =>
                                            this.handleChequeInputChange(rowsKey, index, 'name', e.target.value)
                                        }
                                    />
                                </td>
                                <td>
                                    <Input
                                        placeholder="Cheque number"
                                        value={row.cheque_number || ''}
                                        onChange={(e) =>
                                            this.handleChequeInputChange(
                                                rowsKey,
                                                index,
                                                'cheque_number',
                                                e.target.value
                                            )
                                        }
                                    />
                                </td>
                                {_showAmount && (
                                    <td>
                                        <Input
                                            value={row.emi_amount || ''}
                                            onChange={(e) =>
                                                this.handleChequeInputChange(
                                                    rowsKey,
                                                    index,
                                                    'emi_amount',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {showSave && (
                    <div className="nach-mandate-btn-row nach-mandate-submit-row">
                        <Button
                            type="primary"
                            loading={saveLoading}
                            disabled={saveDisabled}
                            onClick={onSave}
                        >
                            Save cheque details
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    renderActionButtons = () => {
        const {
            loading,
            documentUploadStatus,
            schemeType,
            user,
            categoryJourney,
            isTermLoan,
            emiCount,
            nachRequestType,
        } = this.props;
        const mandateType = this.getEffectiveMandateType();
        const modeJourney = this.getSelectedModeJourney();
        const nachDetails = this.getNachDetails();
        const status =
            modeJourney && modeJourney.status != null && modeJourney.status !== ''
                ? Number(modeJourney.status)
                : nachDetails && nachDetails.nach_status != null && nachDetails.nach_status !== ''
                ? Number(nachDetails.nach_status)
                : null;
        const canTrigger = modeJourney && modeJourney.can_trigger;
        const disableTrigger =
            nachDetails &&
            isSuccessStatus(nachDetails.nach_status) &&
            nachDetails.mandate_type === mandateType;

        const disabledPerm =
            [57, 11, 10].includes(this.props.currentCaseTaskStageId?.[0]?.this_stage) &&
            this.props.user?.groups?.find((el) => el.id === 68);

        if (this.props.form.getFieldValue('associate') == null) return null;

        const getCancelActionLabel = (statusCode) => {
            const s = Number(statusCode);
            if (s === 1) return 'Reject';
            if (s === 4 || s === 5) return 'Cancel Nach';
            return 'Cancel Nach';
        };

        const showCancelAction =
            disableTrigger ||
            (status != null && !Number.isNaN(status) && canCancelStatus(status));

        if (mandateType === MANDATE_API) {
            const { resultRow } = this.props;
            const apiDetails =
                (nachDetails && nachDetails.mandate_type === MANDATE_API && nachDetails) ||
                (resultRow &&
                    resultRow.nach_details &&
                    resultRow.nach_details.mandate_type === MANDATE_API &&
                    resultRow.nach_details) ||
                null;
            const nachUrl =
                (apiDetails && apiDetails.url) ||
                (apiDetails &&
                    apiDetails.digio_respone &&
                    apiDetails.digio_respone.data &&
                    apiDetails.digio_respone.data.redirect_url) ||
                null;
            return (
                <Spin spinning={loading}>
                    <div className="nach-mandate-actions-inner">
                        <div className="nach-mandate-btn-row">
                            <Button
                                type="primary"
                                disabled={disableTrigger || !canTrigger}
                                onClick={(e) => this.getCaseEnachDetails(e, MANDATE_API)}
                            >
                                Trigger
                            </Button>
                            {showCancelAction && (
                                <Button
                                    disabled={!disabledPerm}
                                    loading={this.state.cancelNachLoader}
                                    onClick={this.cancelNachMandate}
                                >
                                    {getCancelActionLabel(status)}
                                </Button>
                            )}
                            {status != null && (
                                <Button loading={this.state.pollLoader} onClick={this.pollDigioStatus}>
                                    Check Status
                                </Button>
                            )}
                            {status !== 2 && <Button
                                type="link"
                                disabled={!nachUrl}
                                onClick={() => this.copyToClipboard(nachUrl)}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                            </Button>}
                            {/* {this.renderInlineStatusBadge()} */}
                        </div>
                    </div>
                </Spin>
            );
        }

        if (mandateType === MANDATE_PHYSICAL) {
            const docStatus = documentUploadStatus ? 'Document Uploaded' : 'Document Pending';
            const physicalMandateId = this.getPhysicalMandateId();
            const bankNachId = this.getBankNachId();
            const { physicalCheques, physicalGenerated, physicalChequesSaveLoader } = this.state;
            const chequeLabel =
                nachRequestType === 'EMI-1' || nachRequestType === 'EMI-2'
                    ? `EMI cheques — ${physicalCheques.length} rows`
                    : isTermLoan && nachRequestType !== 'SECURITY'
                    ? `EMI Cheques — ${physicalCheques.length} rows`
                    : `Physical cheques — ${physicalCheques.length} rows`;
            const showPhysicalChequeForm =
                !!bankNachId ||
                physicalGenerated ||
                (modeJourney && modeJourney.status != null) ||
                (nachDetails && nachDetails.mandate_type === MANDATE_PHYSICAL);
            const amountRequired = nachRequestType !== 'SECURITY';
            const canSaveCheques =
                !!bankNachId &&
                !physicalCheques.some((row) => {
                    const rawAmount = row.emi_amount != null ? row.emi_amount : row.cheque_amount;
                    const amountMissing =
                        amountRequired &&
                        (rawAmount == null ||
                            String(rawAmount).trim() === '' ||
                            !(Number(rawAmount) > 0));
                    return !row.name || !row.cheque_number || amountMissing;
                });

            const { resultRow } = this.props;
            const physicalDetails =
                (nachDetails && nachDetails.mandate_type === MANDATE_PHYSICAL && nachDetails) ||
                (resultRow &&
                    resultRow.nach_details &&
                    resultRow.nach_details.mandate_type === MANDATE_PHYSICAL &&
                    resultRow.nach_details) ||
                null;
            const physicalNachUrl =
                (physicalDetails && physicalDetails.url) ||
                (physicalDetails &&
                    physicalDetails.digio_respone &&
                    physicalDetails.digio_respone.data &&
                    physicalDetails.digio_respone.data.redirect_url) ||
                null;

            return (
                <Spin spinning={loading}>
                    <div className="nach-mandate-actions-inner">
                        <div className="nach-mandate-btn-row">
                            <Button
                                type="primary"
                                disabled={!canTrigger || disableTrigger}
                                onClick={(e) => this.getCaseEnachDetails(e, MANDATE_PHYSICAL)}
                            >
                                Trigger
                            </Button>
                            <Tag color="magenta">{docStatus}</Tag>
                            <Button
                                type="link"
                                loading={this.state.physicalDownloadLoader}
                                disabled={!physicalMandateId}
                                onClick={this.downloadPhysicalForm}
                            >
                                Download Physical form
                            </Button>
                            <Spin spinning={this.state.uploadLoader}>
                                <Upload
                                    beforeUpload={this.handlePhysicalUpload}
                                    multiple={false}
                                    fileList={this.state.fileList}
                                    showUploadList={false}
                                >
                                    <Button disabled={!disabledPerm}>
                                        <Icon type="upload" /> Click to Select File
                                    </Button>
                                </Upload>
                            </Spin>
                            {showCancelAction && (
                                <Button
                                    disabled={!disabledPerm}
                                    loading={this.state.cancelNachLoader}
                                    onClick={this.cancelNachMandate}
                                >
                                    {getCancelActionLabel(status)}
                                </Button>
                            )}
                            {status != null && (
                                <Button loading={this.state.pollLoader} onClick={this.pollDigioStatus}>
                                    Check Status
                                </Button>
                            )}
                            {status !== 2 && (
                                <Button
                                    type="link"
                                    disabled={!physicalNachUrl}
                                    onClick={() => this.copyToClipboard(physicalNachUrl)}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </Button>
                            )}
                            {/* {this.renderInlineStatusBadge()} */}
                        </div>
                        {showPhysicalChequeForm &&
                            this.renderChequeTable('physicalCheques', physicalCheques, {
                                showEmiDate:
                                    (isTermLoan && nachRequestType !== 'SECURITY') ||
                                    physicalCheques.some((r) => r && r.due_date != null && r.due_date !== ''),
                                showAmount: nachRequestType !== 'SECURITY',
                                sectionLabel: chequeLabel,
                                showSave: !!bankNachId,
                                onSave: this.savePhysicalChequeDetails,
                                saveLoading: physicalChequesSaveLoader,
                                saveDisabled: !canSaveCheques,
                            })}
                        {showPhysicalChequeForm && !bankNachId && (
                            <p className="text-muted nach-mandate-step-hint">
                                Trigger Physical NACH first, then enter and save cheque details.
                            </p>
                        )}
                    </div>
                </Spin>
            );
        }

        if (mandateType === MANDATE_NO_PDC) {
            const allowPdc =
                schemeType === 'Term Loan' ||
                schemeType === 'Default Scheme' ||
                [279, 252, 439, 195605, 440, 177657, 232, 384, 423, 436, 273].includes(user?.id);

            if (!allowPdc) {
                return (
                    <div className="nach-mandate-actions-inner">
                        <h5>No Nach PDC is disabled for Non Term Loan</h5>
                    </div>
                );
            }

            const chequeCount = getNoNachPdcRowCount(
                nachRequestType,
                this.props.instalmentFrequency,
                this.props.totalEmiCount
            );
            const pdcAlertMessage =
                nachRequestType === 'SECURITY'
                    ? `PDC details are mandatory before proceeding from PS Kit Review. Security: ${chequeCount} cheques.`
                    : 'PDC details are mandatory before proceeding from PS Kit Review.';
            const { pdcGenerated, pdcCheques } = this.state;
            // Generate is disabled for Initiated (1), Verified (4), and Bank Confirmed (5).
            const generateBlockingStatuses = [1, 4, 5];
            const statusBlocksGenerate = generateBlockingStatuses.includes(Number(status));
            // PDC cheque table is shown only when status is Initiated (1) or Verified (4)
            const securityStatusAllowsTable =
                !['SECURITY', 'EMI-1', 'EMI-2'].includes(nachRequestType) ||
                [1, 4].includes(Number(status));
            const showPdcTable =
                securityStatusAllowsTable && (pdcGenerated || pdcCheques.length > 0);
            // Hide previously uploaded EML when the mandate is cancelled / rejected / failed
            const isCancelledRejectedFailed = [2, 3, 6].includes(Number(status));
            const existingEmlUrl = isCancelledRejectedFailed
                ? null
                : this.getExistingEmlDocumentUrl();
            const hasEml = !!(this.state.emlFile || existingEmlUrl);
            const canTriggerNoNachPdc =
                categoryJourney && categoryJourney.can_trigger_no_nach_pdc !== false;
            const emlFileName = existingEmlUrl
                ? decodeURIComponent(
                      (existingEmlUrl.split('/').pop() || 'Uploaded EML file').split('?')[0]
                  )
                : null;
            return (
                <Spin spinning={this.state.loading || loading}>
                    <div className="nach-mandate-actions-inner nach-mandate-no-pdc">
                        <Alert
                            type="warning"
                            showIcon
                            className="nach-mandate-pdc-alert"
                            message={pdcAlertMessage}
                        />
                        <div className="nach-mandate-step">
                            <div className="nach-mandate-step-title">Step 1 · Upload EML file</div>
                            <p className="text-muted nach-mandate-step-hint">
                                EML attachment is mandatory to enable the Generate action.
                            </p>
                            <div className="nach-mandate-btn-row">
                                <Upload
                                    beforeUpload={this.handleEmlUpload}
                                    fileList={this.state.fileList}
                                    showUploadList={false}
                                >
                                    <Button>
                                        <Icon type="paper-clip" /> Click to Select EML file
                                    </Button>
                                </Upload>
                                <Tag color={hasEml ? 'green' : 'gold'}>
                                    {hasEml ? 'EML uploaded' : 'EML pending'}
                                </Tag>
                            </div>
                            {existingEmlUrl && (
                                <div className="nach-mandate-eml-uploaded mt-10">
                                    <span className="text-muted">Uploaded EML: </span>
                                    <a
                                        href={existingEmlUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download
                                    >
                                        {emlFileName}
                                    </a>
                                </div>
                            )}
                        </div>
                        <div className="nach-mandate-step">
                            <div className="nach-mandate-step-title">Step 2 · Generate PDC rows</div>
                            <div className="nach-mandate-btn-row">
                                <ConfirmButton
                                    label="Generate"
                                    disabled={!hasEml || statusBlocksGenerate}
                                    onConfirm={this.triggerNoNachPdc}
                                    description="Are you sure you want to Trigger No Nach PDC?"
                                    type="primary"
                                />
                                {!hasEml && (
                                    <span className="text-muted nach-mandate-step-hint">
                                        Upload EML file to enable
                                    </span>
                                )}
                                {showCancelAction && (
                                    <Button
                                        disabled={!disabledPerm}
                                        loading={this.state.cancelNachLoader}
                                        onClick={this.cancelNachMandate}
                                    >
                                        {getCancelActionLabel(status)}
                                    </Button>
                                )}
                            </div>
                        </div>
                        {!showPdcTable && !pdcGenerated && !pdcCheques.length && (
                            <div className="nach-mandate-placeholder">
                                Cheque Number &amp; Beneficiary Name fields will appear here after Generate is
                                clicked.
                            </div>
                        )}
                        {showPdcTable &&
                            this.renderChequeTable('pdcCheques', pdcCheques, {
                                showEmiDate: isTermLoan && nachRequestType !== 'SECURITY',
                                showAmount: nachRequestType !== 'SECURITY',
                                sectionLabel: `PDC cheques — ${pdcCheques.length} rows`,
                            })}
                        {showPdcTable && (
                            <div className="nach-mandate-btn-row nach-mandate-submit-row">
                                <Button
                                    type="primary"
                                    disabled={
                                        (nachRequestType === 'SECURITY' &&
                                            [4, 5].includes(Number(status))) ||
                                        pdcCheques.filter((_) => {
                                            const rawAmount =
                                                _?.emi_amount != null ? _.emi_amount : _?.cheque_amount;
                                            const amountMissing =
                                                nachRequestType !== 'SECURITY' &&
                                                (rawAmount == null ||
                                                    String(rawAmount).trim() === '' ||
                                                    !(Number(rawAmount) > 0));
                                            return !_?.name || !_?.cheque_number || amountMissing;
                                        }).length > 0
                                    }
                                    onClick={this.updateNoNachPDC}
                                >
                                    Submit
                                </Button>
                            </div>
                        )}
                    </div>
                </Spin>
            );
        }

        return null;
    };

    renderMandateSummary = () => {
        const { categoryJourney, resultRow } = this.props;
        const mandateType = this.getEffectiveMandateType();
        const summary = buildMandateSummary(categoryJourney, mandateType, resultRow);
        if (!summary || mandateType == null) return null;
        return (
            <Card className="nach-mandate-summary-card" bordered>
                <Descriptions bordered size="small" column={3}>
                    <Descriptions.Item label="Mandate">{summary.mandate_id}</Descriptions.Item>
                    <Descriptions.Item label="Mode">{summary.mode}</Descriptions.Item>
                    <Descriptions.Item label="Transferred to">{summary.transferred_to}</Descriptions.Item>
                    <Descriptions.Item label="UMRN">{summary.umrn}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag color={getStatusTagColor(summary.statusCode)}>
                            {getDisplayStatusLabel(summary.statusCode)}
                        </Tag>
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        );
    };

    render() {
        const { sectionTitle, sectionSubtitle, categoryJourney, caseAssociates } = this.props;
        const dropdownOptions = buildModeDropdownOptions(categoryJourney);
        const associateDisabled = !(
            [57, 11, 10].includes(this.props.currentCaseTaskStageId?.[0]?.this_stage) &&
            this.props.user?.groups?.find((el) => el.id === 68)
        );

        const associateOptions = (caseAssociates || []).map((a) => ({
            value: a.company_associate.id,
            label: `${a.company_associate.first_name}${a.is_main_borrower ? ' (Main Borrower)' : ''}`,
        }));

        const mandateType = this.getEffectiveMandateType();
        const sectionGuide = getNachSectionTooltip(
            this.props.nachRequestType,
            this.props.isTermLoan,
            this.props.isLoanAbove1Cr
        );

        return (
            <Form
                layout="vertical"
                className={cx('form', 'p-10', 'br-3', 'nach-mandate-form')}
                onValuesChange={(changed) => {
                    if (changed.mandate_type != null) {
                        this.onMandateTypeChange(changed.mandate_type);
                    }
                }}
            >
                <div className="nach-mandate-section-header">
                    <h3 className="nach-mandate-section-heading">
                        {sectionTitle}
                        {sectionSubtitle && (
                            <Tag className="nach-mandate-section-tag">{sectionSubtitle}</Tag>
                        )}
                        {this.renderStatusTag()}
                    </h3>
                    {sectionGuide && (
                        <div className="nach-mandate-section-process">
                            {sectionGuide.type === 'single' ? (
                                <p className="nach-mandate-process-note">{sectionGuide.text}</p>
                            ) : (
                                <ul className="nach-mandate-process-list">
                                    {sectionGuide.items.map((item) => (
                                        <li key={item.label}>
                                            <span className="nach-mandate-process-label">{item.label}</span>
                                            <span className="nach-mandate-process-text">{item.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
                {mandateType === MANDATE_API && (
                    <p className="text-muted nach-mandate-helper" style={{ marginBottom: 12 }}>
                        Auto-triggered on Main Borrower.
                        {this.props.isLoanAbove1Cr && ' Not available for loans > 1 Cr.'}
                    </p>
                )}
                <div className="form-row nach-mandate-form-row">
                    {this.fieldGenerator(
                        'mandate_type',
                        {
                            className: 'form-field fb-33',
                            type: INPUT_TYPES.SELECT,
                            label: 'NACH Mode',
                            placeholder: 'NACH Mode',
                            options: dropdownOptions.map((opt) => {
                                return ({
                                    value: opt.value,
                                    label: opt.is_locked ? `${opt.label} 🔒` : opt.label,
                                    rest: {
                                        disabled: opt.is_locked,
                                        title: opt.lock_reason,
                                    },
                                });
                            }),
                            rules: [{ required: true, message: 'Field is required' }],
                            onChange: (val) => this.onMandateTypeChange(val),
                        },
                        this.getErrorOnField('mandate_type')
                    )()}
                    {this.fieldGenerator(
                        'associate',
                        {
                            className: 'form-field fb-33',
                            type: INPUT_TYPES.SELECT,
                            label: 'Applicant',
                            placeholder: 'Applicant',
                            disabled: associateDisabled,
                            options: associateOptions,
                            rules: [{ required: true, message: 'Field is required' }],
                        },
                        this.getErrorOnField('associate')
                    )()}
                </div>
                <div className="nach-mandate-actions">{this.renderActionButtons()}</div>
                <div className="nach-mandate-summary mt-10">{this.renderMandateSummary()}</div>
            </Form>
        );
    }
}

NachMandateForm.defaultProps = {
    caseAssociates: [],
    bank_account: {},
    categoryJourney: null,
};

NachMandateForm.propTypes = {
    form: PropTypes.object.isRequired,
    values: PropTypes.object,
    caseAssociates: PropTypes.array,
    nachRequestType: PropTypes.string,
    sectionTitle: PropTypes.string,
    sectionSubtitle: PropTypes.string,
    categoryJourney: PropTypes.object,
    resultRow: PropTypes.object,
    emiDate: PropTypes.number,
    isTermLoan: PropTypes.bool,
    isLoanAbove1Cr: PropTypes.bool,
    emiCount: PropTypes.number,
    emiDates: PropTypes.array,
    instalmentFrequency: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalEmiCount: PropTypes.number,
    loading: PropTypes.bool,
    bank_account: PropTypes.object,
    caseId: PropTypes.number,
    schemeType: PropTypes.string,
    onSubmit: PropTypes.func,
    triggerEnach: PropTypes.func,
    updateNachData: PropTypes.func,
    documentUploadStatus: PropTypes.string,
    user: PropTypes.object,
    currentCaseTaskStageId: PropTypes.array,
};

export default Form.create({
    mapPropsToFields: ({ values }) => {
        if (!values) return {};
        return values
            .toSeq()
            .toMap()
            .map((val, key) => Form.createFormField({ value: val }))
            .toJS();
    },
})(NachMandateForm);
